import React, { Dispatch, Component } from "react";
import { Action, AnyAction } from "redux";
import { connect } from "react-redux";
import { ThunkDispatch } from "redux-thunk";
import GoogleMapReact from "google-map-react";
import { RootState, actions } from "../../store";
import Marker from "../Marker/Marker";
import AddressAPI from "../api/addressAPI";
import BottomContainer from "../BottomContainer/BottomContainer";
import TAXI_PARAMS from "../../utils/build";
import { getCurrentLocation } from "../bricks/locationUtils";
import {
  IRoutingParams,
  RoutePoints,
} from "../../webServer/controllers/customer/types/CalculateCost";
import icons from "./icons";
import { AddressCenter, IGetAddressItem } from "sedi-webserverproxy";
import "./Map.scss";
import { routePoints } from "../../utils/routePoints";
import i18next from "i18next";

type Props = ReturnType<typeof mapDispatchToProps> &
  ReturnType<typeof mapStateToProps>;

class Map extends Component<Props, any> {
  addressAPI = new AddressAPI();
  map: React.RefObject<any>;
  markers: google.maps.Marker[] = [];
  directionsRenderer: google.maps.DirectionsRenderer | null = null;
  flightPath: google.maps.Polyline | null = null;

  static defaultProps = {
    center: {
      lat: Number(TAXI_PARAMS.LAT || 55.755825),
      lon: Number(TAXI_PARAMS.LNG || 37.617298),
    },
    zoom: 16,
    options: {
      maxZoom: 19,
      fullscreenControl: false,
      zoomControl: false,
    },
  };

  constructor(props: Props) {
    super(props);
    this.map = React.createRef();
    this.state = {
      mapOptions: {
        zoom: Map.defaultProps.zoom,
      },
      routeLegs: {
        text: "",
        value: "",
      },
      pointCenter: null,
      clipMarker: true,
    };
  }

  makeMarker(
    position: google.maps.LatLng,
    iconUrl: string,
    map: any,
    selectedPoint: number
  ) {
    const { selectedPointAction } = this.props;

    const marker = new google.maps.Marker({
      position,
      map,
      icon: { url: iconUrl, rotation: 90 },
      label: {
        text: `${i18next.t("changeDropPoint")} ${
          selectedPoint === 1 ? "A" : "B"
        }`,
        fontSize: "13px",
        color: "#444",
      },
    });

    this.setState({ clipMarker: false });

    marker.addListener("click", () => {
      selectedPointAction(selectedPoint);
      this.setState({ clipMarker: true });

      if (this.state.pointCenter) {
        this.setState({ pointCenter: marker.getPosition() as google.maps.LatLng });
      }

      if (map) map.setCenter(marker.getPosition() as google.maps.LatLng);
    });

    this.markers.push(marker);
  }

  deleteMarkers() {
    this.markers.forEach((m) => m.setMap(null));
    this.markers = [];
  }

  getRoutingInfo() {
    const {
      origin,
      destination,
      getRoutingInfoAction,
      getRouteProcessingAction,
    } = this.props;

    if (this.directionsRenderer) {
      this.directionsRenderer.setMap(null);
      this.deleteMarkers();
    }

    if (origin && destination) {
      const routeInfoParams = {
        orderTime: new Date(),
        routePoints: [
          { latitude: origin.lat, longitude: origin.lon },
          { latitude: destination.lat, longitude: destination.lon },
        ],
      };

      getRouteProcessingAction(true);
      getRoutingInfoAction(routeInfoParams).then(() => {
        getRouteProcessingAction(false);
      });
    }
  }

  fitBoundsByMarkers() {
    const { origin, destination } = this.props;
    if (!this.map.current?.map_) return;

    const mapRef = this.map.current.map_;
    if (origin && destination) {
      const bounds = new google.maps.LatLngBounds();
      bounds.extend(new google.maps.LatLng(origin.lat, origin.lon));
      bounds.extend(new google.maps.LatLng(destination.lat, destination.lon));
      mapRef.fitBounds(bounds);
    }
  }

  routeMap() {
    const { origin, destination, carInWay, routingInfo } = this.props;

    try {
      if (!this.map.current?.map_ || !routingInfo) return;

      const mapRef = this.map.current.map_;

      if (origin && destination) {
        if (this.flightPath) this.flightPath.setMap(null);

        const { routePoints: routePts } = routingInfo;

        this.flightPath = new google.maps.Polyline({
          path: [
            new google.maps.LatLng(origin.lat, origin.lon),
            ...routePts.map(
              (p: any) => new google.maps.LatLng(p.latitude, p.longitude)
            ),
            new google.maps.LatLng(destination.lat, destination.lon),
          ],
          geodesic: true,
          strokeColor: "#FF0000",
          strokeOpacity: 1.0,
          strokeWeight: 2,
        });

        this.flightPath.setMap(mapRef);

        this.makeMarker(
          new google.maps.LatLng(origin.lat, origin.lon),
          icons.start.url,
          mapRef,
          1
        );

        this.makeMarker(
          new google.maps.LatLng(destination.lat, destination.lon),
          carInWay ? icons.car.url : icons.end.url,
          mapRef,
          2
        );

        this.fitBoundsByMarkers();
      }
    } catch (error) {
      console.log("routingInfo error:", error);
    }
  }

  showCurrentLocation() {
    const { addressCenterAction } = this.props;
    let infoWindow: google.maps.InfoWindow | null = null;

    if (typeof google !== "undefined") {
      infoWindow = new google.maps.InfoWindow();
    }

    getCurrentLocation({
      enableHighAccuracy: true,
      timeout: 5000,
      maximumAge: 0,
    }).then((result: any) => {
      const center: AddressCenter = {
        lat: result.coords.latitude,
        lon: result.coords.longitude,
      };

      if (infoWindow) {
        infoWindow.setPosition({
          lat: result.coords.latitude,
          lng: result.coords.longitude,
        });
      }

      if (this.map.current?.map_) {
        this.map.current.map_.setCenter({
          lat: result.coords.latitude,
          lng: result.coords.longitude,
        });
      }

      addressCenterAction(center);
    });
  }

  componentDidMount() {
    this.showCurrentLocation();
  }

  componentDidUpdate(prevProps: Props) {
    const {
      valueFromViewAction,
      center,
      setRoutePointAction,
      changePointAutocomplate,
      destination,
      routeReset,
      carInWay,
      route,
      currentAddressAction,
      routingInfo,
      lang,
    } = this.props;

    const { lat, lon } = this.props.center;

    if (center !== prevProps.center && this.map.current?.map_) {
      this.map.current.map_.setCenter({ lat, lng: lon });
    }

    if (center !== prevProps.center && changePointAutocomplate === false) {
      this.addressAPI.getLocation(lang, lat, lon).then((data) => {
        const addr = data[0].address;
        let type = addr.house_number
          ? "h"
          : addr.building || addr.leisure || addr.amenity
          ? "o"
          : addr.road
          ? "s"
          : "c";

        const routePoint: IGetAddressItem = {
          co: addr.country,
          c: addr.city,
          cid: 0,
          s: addr.road || data[0].display_name,
          n: "",
          h: addr.house_number,
          g: { lat: data[0].lat, lon: data[0].lon },
          t: type,
          v: addr.house_number || addr.building || addr.leisure || addr.amenity || "",
        };

        setRoutePointAction(routePoint);
        currentAddressAction(routePoint);
        const ruResult = routePoints(routePoint);
        if (ruResult) valueFromViewAction(ruResult);
      });
    }

    if (destination !== prevProps.destination || carInWay !== prevProps.carInWay) {
      this.getRoutingInfo();
    }

    if (routingInfo !== prevProps.routingInfo) {
      this.routeMap();
    }

    if (
      routeReset !== prevProps.routeReset ||
      (route.point1 && !route.point1.s && !route.point1.v) ||
      (route.point2 && !route.point2.s && !route.point2.v)
    ) {
      if (this.directionsRenderer) this.directionsRenderer.setMap(null);
      this.deleteMarkers();
      if (this.flightPath) this.flightPath.setMap(null);
      window.location.href = "/";
    }
  }

  getMapBounds(lat: number, lng: number) {
    const bounds = new google.maps.LatLngBounds();
    bounds.extend(new google.maps.LatLng(lat, lng));
    return bounds;
  }

  bindResizeListener(map: any, maps: any, bounds: google.maps.LatLngBounds) {
    maps.event.addDomListenerOnce(map, "idle", () => {
      maps.event.addDomListener(window, "resize", () => {
        map.fitBounds(bounds);
      });
    });
  }

  apiIsLoaded(map: any, maps: any, lat: number, lng: number) {
    // создаём DirectionsRenderer после загрузки API
    this.directionsRenderer = new maps.DirectionsRenderer({
      suppressMarkers: true,
    });

    const bounds = this.getMapBounds(lat, lng);
    this.bindResizeListener(map, maps, bounds);
  }

  handleLocationClick = () =>
    getCurrentLocation().then((result: any) => {
      const lat = result.coords.latitude;
      const lng = result.coords.longitude;
      const { valueFromViewAction, lang } = this.props;

      if (!this.map.current?.map_) return;

      const bounds = this.getMapBounds(lat, lng);
      this.map.current.map_.fitBounds(bounds);
      this.map.current.map_.setZoom(16);

      this.addressAPI.getLocation(lang, lat, lng).then((data) => {
        const addr = data[0].address;
        let type = addr.house_number
          ? "h"
          : addr.building || addr.leisure || addr.amenity
          ? "o"
          : addr.road
          ? "s"
          : "c";

        const routePoint: IGetAddressItem = {
          co: addr.country,
          c: addr.city,
          cid: 0,
          s: addr.road || data[0].display_name,
          n: "",
          h: addr.house_number,
          g: { lat: data[0].lat, lon: data[0].lon },
          t: type,
          v: addr.house_number || addr.building || addr.leisure || addr.amenity || "",
        };

        this.props.setRoutePointAction(routePoint);
        this.props.currentAddressAction(routePoint);

        const ruResult = routePoints(routePoint);
        if (ruResult) valueFromViewAction(ruResult);
      });
    });

  onDragEndMap(map: any) {
    const { addressCenterAction, setPointAutocomplateAction, destination, route } =
      this.props;

    const newCenter: AddressCenter = {
      lat: map.center.lat(),
      lon: map.center.lng(),
    };

    if (route.point2) {
      this.setState({ pointCenter: newCenter });
    }

    if (destination && destination.lat > 0) return;

    setPointAutocomplateAction(false);
    addressCenterAction(newCenter);
  }

  changePointToHandle(pointCenter: AddressCenter | null) {
    this.setState({ pointCenter });
  }

  clipMarkerHandle(clipMarker: boolean) {
    this.setState({ clipMarker });
  }

  render() {
    const { footerHeight, center } = this.props;
    const footerBoxHeight = `calc(100vh - ${footerHeight}px)`;

    return (
      <div className="map" style={{ height: footerBoxHeight }}>
        <GoogleMapReact
          bootstrapURLKeys={{ key: TAXI_PARAMS.GOOGLE_MAPS_API_KEY || "" }}
          defaultCenter={{ lat: center.lat, lng: center.lon }}
          defaultZoom={Map.defaultProps.zoom}
          options={Map.defaultProps.options}
          yesIWantToUseGoogleMapApiInternals
          onGoogleApiLoaded={({ map, maps }) =>
            this.apiIsLoaded(map, maps, center.lat, center.lon)
          }
          draggable={true}
          onDragEnd={(map) => this.onDragEndMap(map)}
          ref={this.map}
        />
        <Marker
          pointCenter={this.state.pointCenter}
          changePointToHandle={(p) => this.changePointToHandle(p)}
          clipMarker={this.state.clipMarker}
          clipMarkerHandle={(c) => this.clipMarkerHandle(c)}
        />
        <BottomContainer handleLocationClick={this.handleLocationClick} />
      </div>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  center: state.map.center,
  valueFromView: state.map.valueFromView,
  footerHeight: state.map.footerHeight,
  route: state.map.route,
  changePointAutocomplate: state.map.changePointAutocomplate,
  origin: state.map.origin,
  destination: state.map.destination,
  successAddOrder: state.map.successAddOrder,
  routeReset: state.map.routeReset,
  carInWay: state.map.carInWay,
  routingInfo: state.map.routingInfo,
  lang: state.map.lang,
});

const mapDispatchToProps = (
  dispatch: Dispatch<Action> & ThunkDispatch<any, any, AnyAction>
) => ({
  valueFromViewAction: (valueFromView: string) =>
    dispatch(actions.map.valueFromViewAction(valueFromView)),
  addressCenterAction: (center: AddressCenter) =>
    dispatch(actions.map.addressCenterAction(center)),
  setRoutePointAction: (route: IGetAddressItem) =>
    dispatch(actions.map.setRoutePointAction(route)),
  setPointAutocomplateAction: (changePointAutocomplate: boolean) =>
    dispatch(actions.map.setPointAutocomplateAction(changePointAutocomplate)),
  setRoutePoints: (points: RoutePoints[]) =>
    dispatch(actions.map.setRoutePoints(points)),
  currentAddressAction: (currentAddress: IGetAddressItem) =>
    dispatch(actions.map.currentAddressAction(currentAddress)),
  getRoutingInfoAction: (parameter: IRoutingParams | null) =>
    dispatch(actions.map.getRoutingInfoAction(parameter)),
  selectedPointAction: (selectedPoint: number) =>
    dispatch(actions.map.selectedPointAction(selectedPoint)),
  getRouteProcessingAction: (getRouteProcessing: boolean) =>
    dispatch(actions.registration.getRouteProcessingAction(getRouteProcessing)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Map);
